import React, { useMemo, useState } from 'react'
import { isFeatureEnabled } from '../../../../../_helpers/flags'
import promiseMemoize from 'promise-memoize'

// Services
import {
  getAllInvoiceCount,
  getScheduleInvoices,
  searchDebtors,
  updateInvoice,
} from '../../../../../_services/lender.service'
import { CreateDebtor } from '../../../../../_components/modals/createDebtor/CreateDebtor'
import { getDebtor } from '../../../../../_services/debtor.service'

// Components
import { RemoveScheduleInvoice } from './RemoveScheduleInvoice'
import { EditScheduleTableControls } from './EditScheduleTableControls'
import { SearchableOverview } from '../../../../../_components/SearchableOverview'
import { EditableTable } from '../../../../../_components/EditableTable'
import { S_LinkCell } from '../../../../ClientViews/UploadInvoices/S_UploadInvoices'
import { InvoiceFilesModal } from '../../../lender/ScheduleDetails/InvoiceFilesModal/InvoiceFilesModal'

// Helpers
import { currencyToNum, systemNow } from '../../../../../_helpers'
import { getInvoicesErrors, addInvoiceErrorObject, tableFormat } from '../../../../../_helpers/invoices'
import { createSearchParams } from '../../../../../_helpers/search'

// Hooks
import { useFlags } from '../../../../../_reduxHooks/flags.hook'
import { useModal } from '../../../../../_reduxHooks/modal.hook'
import { INVOICES_FILTER_COLUMNS } from '../../../../common/lender/InvoicesOverview/constants'
import { uploadInvoiceSupportingDocument } from '../../../../../_services/invoices.service'

// Helpers for debtors search
const debtorValueFetch = promiseMemoize(getDebtor)
const fetchDebtorSuggestions = async (searchText, business) => {
  let searchParams = createSearchParams({
    searchTerm: searchText,
    searchParam: 'debtor_name',
  })
  if (business.client_debtor_relation_required) {
    searchParams.set('client_debtor_relation_required', business.id)
  }
  searchParams.set('type', 'tenant')
  let debtors = await searchDebtors(searchParams.toString())
  return debtors
}

const getEditableInvoicesColumns = (business, isClientView, fundCheckbox, openModal) => {
  const columns = [
    {
      id: 'debtor_id',
      label: 'Debtor',
      type: 'autocomplete',
      validate: (id) => {
        if (!id) {
          return 'Debtor is Required'
        }
        return null
      },
      autoCompleteProps: {
        checkInputValue: false,
        valueIdAttr: 'id',
        valueLabelAttr: 'debtor_name',
        fetchRecord: debtorValueFetch,
        fetchSuggestions: (searchText) => fetchDebtorSuggestions(searchText, business),
        defaultSuggestion: `Start typing to find debtor.`,
        addButton: business && !business.client_debtor_relation_required,
        buttonModal: CreateDebtor,
        modalText: `Add New Debtor`,
      },
      inSpreadsheet: true,
      spreadSheetColumns: ['Debtor ID', 'Debtor Name'],
    },
    {
      id: 'invoice_number',
      label: 'Invoice #',
      type: 'text',
      validate: (value) => {
        if (!value) {
          return 'Invoice # is Required'
        }
        return null
      },
      inSpreadsheet: true,
    },
    {
      id: 'po_number',
      label: 'Purchase Order',
      type: 'text',
      validate: () => {
        return null
      },
      inSpreadsheet: true,
    },
    {
      id: 'invoice_date',
      label: 'Invoice Date',
      type: 'date',
      validate: (datestring) => {
        if (!datestring) {
          return 'Invoice Date is Required'
        }
        let dateParts = datestring.split('-')
        let year = Number(dateParts[0])
        let month = Number(dateParts[1]) - 1
        let day = Number(dateParts[2])
        let dateForCompare = new Date(year, month, day)
        let now = systemNow()
        if (now.getTime() < dateForCompare.getTime()) {
          return 'Invoice Date must be in the past'
        }
        if (business.max_invoice_age_for_purchase) {
          let differenceMs = now.getTime() - dateForCompare.getTime()
          let age = Math.ceil(differenceMs / (1000 * 60 * 60 * 24))
          if (age > business.max_invoice_age_for_purchase) {
            return `Invoice is over age limit (${business.max_invoice_age_for_purchase}).`
          }
        }
        return ''
      },
      inSpreadsheet: true,
    },
    {
      id: 'payment_terms',
      label: 'Terms',
      type: 'integer',
      defaultValue: 30,
      max: business?.max_invoice_term || 120,
      validate: (num) => {
        if (!num) {
          return 'Due Date is required'
        }
        if (num <= 0) {
          return 'Due Date must be greater than 0'
        }
        if (business.max_invoice_term && num > business.max_invoice_term) {
          return `Invoice term is over max allowed (${business.max_invoice_term}).`
        }
        return ''
      },
      inSpreadsheet: true,
    },
    {
      id: 'face_value',
      label: 'Amount',
      type: 'currency',
      validate: (currencyString) => {
        let num = typeof currencyString == 'string' ? currencyToNum(currencyString || '$0.00') : currencyString
        if (!(num > 0)) {
          return 'Invoice Amount must be greater than 0'
        }
        return ''
      },
      inSpreadsheet: true,
    },
  ]

  if (!isClientView) {
    columns.push({
      id: 'supporting_documents_count',
      label: 'files',
      type: 'text',
      Cell: (props) => {
        const { entity } = props

        return (
          <S_LinkCell
            onClick={() => {
              openModal(InvoiceFilesModal, {
                invoiceId: props.entity.id,
              })
            }}
          >
            {entity['supporting_documents_count']}
          </S_LinkCell>
        )
      },
      validate: () => {
        return null
      },
      required: false,
      disabled: true,
    })
  }

  if (fundCheckbox && !isClientView) {
    columns.splice(columns.length - 1, 0, {
      id: 'fund',
      label: 'Fund',
      headingType: 'tooltip',
      headingTooltip:
        'Unchecking this box will input the invoices into Xen but not mark them as funded against. They will not be calculated in the clients Outstanding Invoices or NFE. Factoring Terms will not apply to non funded invoices.',
      type: 'checkbox',
      validate: () => {
        return null
      },
      style: { maxWidth: '80px' },
      defaultValue: true,
      inSpreadsheet: false,
    })
  }
  return columns
}

const getFilterColumns = (filters) => {
  const columnsToRemove = ['ineligible', 'state', 'business_id', 'sentiment']
  return filters.filter((filter) => {
    return !columnsToRemove.includes(filter.fieldName)
  })
}

export const EditScheduleTable = React.memo(
  ({
    invoiceSchedule,
    business,
    setBatchTotal,
    setFundedTotal,
    reloadSchedulePage,
    isLoading,
    setIsLoading,
    setInvoicesCount,
    setInvoicesErrors,
    isClientView,
    disabled,
    setTotalInvoices,
  }) => {
    const { featureFlags } = useFlags()
    const { openModal } = useModal()
    const [lastDebtor, setLastDebtor] = useState('')
    const [updateTable, setUpdateTable] = useState(false)
    const [startIndex, setStartIndex] = useState(0)
    const [refreshInvoicesTable, setRefreshInvoicesTable] = useState(false)

    const columns = useMemo(() => {
      const fundCheckbox = isFeatureEnabled('fund_checkbox', featureFlags)
      return getEditableInvoicesColumns(business, isClientView, fundCheckbox, openModal)
    }, [business.id])

    const handleChangeInvoice = (entityId, fieldId, value, prevValue, entity, handleUpdateEntity) => {
      if (fieldId === 'fund') {
        const updatedFund = {
          non_factored: !value,
          non_funded: !value,
        }

        const currentInvoiceAmount = entity.face_value

        setFundedTotal((prev) => {
          return value ? +prev + +currentInvoiceAmount : +prev - +currentInvoiceAmount
        })

        updateScheduleInvoice(entityId, fieldId, updatedFund, handleUpdateEntity, entity)
      }

      if (fieldId === 'face_value') {
        const isFund = entity.fund

        setBatchTotal((prev) => {
          return prev - prevValue + value
        })

        if (isFund) {
          setFundedTotal((prev) => {
            return prev - prevValue + value
          })
        }
      }
    }

    // Handle on blur
    const onBlurHandler = (entityId, fieldId, ev, handleUpdateEntity, entity) => {
      let value = ev.target.value
      // Funding is only cell which we update onChange event
      if (fieldId === 'fund') {
        return
      }
      if (fieldId === 'face_value') {
        value = currencyToNum(ev.target.value)
      }

      if (fieldId === 'debtor_id') {
        setLastDebtor(value)
      }

      updateScheduleInvoice(entityId, fieldId, { [fieldId]: value }, handleUpdateEntity, entity)
    }

    const updateScheduleInvoice = async (entityId, fieldId, data, handleUpdateEntity, entity) => {
      await updateInvoice(entityId, data).then((updatedInvoice) => {
        setInvoicesErrors((errors) => {
          delete errors[entityId]
          return { ...errors, ...addInvoiceErrorObject(updatedInvoice) }
        })
        handleUpdateEntity({ ...entity, ...updatedInvoice })
      })
    }

    // Pagy helpers
    const removeMethod = (invoices, deletedInvoiceId) => {
      const invoiceToRemove = invoices.find((invoice) => invoice.id === deletedInvoiceId)
      const isFund = invoiceToRemove.fund
      const currentInvoiceAmount = invoiceToRemove.face_value
      setInvoicesCount((prev) => prev - 1)
      setBatchTotal((prev) => {
        return prev - currentInvoiceAmount
      })

      if (isFund) {
        setFundedTotal((prev) => {
          return prev - currentInvoiceAmount
        })
      }
      setInvoicesErrors((errors) => {
        delete errors[deletedInvoiceId]
        return { ...errors }
      })
      return invoices.filter((invoice) => invoice.id !== deletedInvoiceId)
    }

    const addNewInvoice = (invoices, newInvoice) => {
      setInvoicesCount((prev) => prev + 1)
      setInvoicesErrors((errors) => {
        const newInvoiceError = addInvoiceErrorObject(newInvoice)
        return { ...errors, ...newInvoiceError }
      })
      return [...invoices, newInvoice]
    }

    const updateInvoiceMethod = (invoices, updatedInvoice) => {
      const index = invoices.findIndex((invoice) => invoice.id === updatedInvoice.id)
      invoices[index] = tableFormat(updatedInvoice)
      return invoices
    }

    const handleUploadSupportingDocument = (acceptedFiles, entityId, entity, handleUpdateEntity) => {
      return uploadInvoiceSupportingDocument(acceptedFiles[0], entityId).then(() => {
        const updatedInvoice = {
          ...entity,
          supporting_documents_count: (entity['supporting_documents_count'] || 0) + 1,
        }
        handleUpdateEntity(updatedInvoice)
      })
    }

    return (
      <SearchableOverview
        allowDownload={false}
        filterColumns={getFilterColumns(INVOICES_FILTER_COLUMNS)}
        searchParam={`invoice_number`}
        searchPlaceholder="Search By Invoice No."
        showSearchBar={true}
        showSearchFilter={true}
        views={[
          {
            title: '',
            endpointCall: (searchParams) => {
              const finalParams = searchParams.toString()
              setStartIndex(+searchParams.get('start_index'))
              return Promise.all([
                getScheduleInvoices(
                  invoiceSchedule.id,
                  `${finalParams}&include_validation=true&include_document_count=true`
                ),
                getAllInvoiceCount(`invoice_schedule_id=${invoiceSchedule.id}&${finalParams}`),
              ]).then(([invoices, counts]) => {
                setInvoicesCount(counts.count)
                const errors = getInvoicesErrors(invoices)
                setInvoicesErrors(errors)
                return {
                  results: invoices.map(tableFormat),
                  total_results: counts.count,
                }
              })
            },
            requestMethod: 'GET',
            columns: columns,
            pageSize: 10,
            removeMethod: removeMethod,
            addMethod: addNewInvoice,
            updateMethod: updateInvoiceMethod,
            fetchReload: (fn) => {
              setRefreshInvoicesTable(() => fn)
            },
            onResultsUpdate: () => {
              setUpdateTable((prev) => !prev)
            },
            editableMode: true,
            Component: ({ results, columns, handleRemove, handleAdd, handleUpdateEntity }) => {
              return (
                <>
                  <EditableTable
                    RowControls={({ entity }) =>
                      results &&
                      results.length > 0 && (
                        <RemoveScheduleInvoice
                          entity={entity}
                          isDisabled={isLoading || disabled}
                          onRemove={handleRemove}
                        />
                      )
                    }
                    customStartIndex={startIndex}
                    entities={results}
                    formFields={columns}
                    key={updateTable}
                    onBlur={(entityId, fieldId, ev, entity) =>
                      onBlurHandler(entityId, fieldId, ev, handleUpdateEntity, entity)
                    }
                    onChange={(entityId, fieldId, value, prevValue, entity) =>
                      handleChangeInvoice(entityId, fieldId, value, prevValue, entity, handleUpdateEntity)
                    }
                    onFilesAccepted={async (acceptedFiles, entityId, entity) => {
                      await handleUploadSupportingDocument(acceptedFiles, entityId, entity, handleUpdateEntity)
                    }}
                    readOnly={isLoading || disabled}
                  />
                  <EditScheduleTableControls
                    addNewInvoice={handleAdd}
                    business={business}
                    columns={columns}
                    disabled={disabled}
                    isLoading={isLoading}
                    lastDebtor={lastDebtor}
                    refreshInvoicesTable={refreshInvoicesTable}
                    reloadSchedulePage={reloadSchedulePage}
                    scheduleId={invoiceSchedule.id}
                    setLoading={setIsLoading}
                    setTotalInvoices={setTotalInvoices}
                  />
                </>
              )
            },
          },
        ]}
      />
    )
  }
)
